import moment from 'moment/moment';
import { pageContext } from 'ctm-util-functions';
import { formatAddress } from '../address/utils.js';
import { setFormValue } from '../form/actions';

/**
 * These utils are provided to assist in updating state for prefill journeys.
 * This is primarily for 11 month email and prefill journeys.
 */

/**
 * Updates the access tokens in state
 * @param {Dispatch} dispatch the Redux event listener for dispatching state updates
 * @param {string} accessToken the account access token
 * @param {string} refreshToken the account refresh token
 */
const updateTokens = (dispatch, accessToken, refreshToken) => {
    dispatch({ type: 'SET_AUTH_TOKENS', access_token: accessToken, refresh_token: refreshToken });
};

/**
 * Updates the journey form data, quote, prefill and vehicle lookup data in state
 * @param {Dispatch} dispatch the Redux event listener for dispatching state updates
 * @param {JourneyPayload} payload The backend api response containing all journey, quote and vehicle lookup data
 */
const updateState = (dispatch, payload) => {
    updateJourney(dispatch, payload);
    updatePrefill(dispatch, payload);
    updateVehicleLookup(dispatch, payload.vehicleLookup);
};

const updateJourney = (dispatch, payload) => {
    const form = payload.form;
    const address = form?.coverDetail?.overnightParking?.address;

    // Set helper to hide 'edit_car_details' link on click_to_edit_car_details
    dispatch({
        type: 'SET_PAGE_VALUE',
        identifier: 'hasBeenPrefilled',
        value: true
    });

    // transaction and control state
    dispatch({ type: 'SET_JOURNEY_PAYLOAD', payload });

    // 11 month assumed questions eg new car claims question
    if (payload?.assumedQuestions?.length && payload?.assumedQuestions?.length > 0) {
        dispatch({ type: 'SET_VALUE', value: payload.assumedQuestions, stateKey: 'vertical.quote', identifier: 'assumedQuestions' });
    }

    // Each action is wrapped for null safety so that we don't break the journey
    // if a user is coming from prefill_verify.
    // form state
    if (form.applicant) {
        dispatch({
            type: 'SET_VALUE',
            value: {
                ...form.applicant,
                optInPrivacy: form.applicant.optInPrivacy ? 'Y' : 'N'
            },
            stateKey: 'vertical.quote',
            identifier: 'applicant'
        });
    }

    if (form?.applicant?.phone !== undefined) {
        dispatch({
            type: 'SET_VALUE',
            value: form.applicant.phone,
            stateKey: 'vertical.quote',
            identifier: 'applicant.phone'
        });
        dispatch({
            type: 'SET_VALUE',
            value: 'Y',
            stateKey: 'vertical.quote',
            identifier: 'helpers.useOtherNumber'
        });
        dispatch({
            type: 'HIDE_FIELD_SET',
            fieldSet: 'applicant.mobile',
            stateKey: 'vertical.quote'
        });
        dispatch({
            type: 'SHOW_FIELD_SET',
            fieldSet: 'applicant.phone',
            stateKey: 'vertical.quote'
        });
    }

    // health lead
    if (form.leadCapture) {
        dispatch({
            type: 'SET_VALUE',
            value: form.leadCapture.health ? 'Y' : 'N',
            stateKey: 'vertical.quote',
            identifier: 'leadCapture.health'
        });
    }

    if (form.coverDetail) {
        dispatch({
            type: 'SET_VALUE',
            value: form.coverDetail,
            stateKey: 'vertical.quote',
            identifier: 'coverDetail'
        });
        if (form.coverDetail?.driver?.licenceAge !== undefined) {
            dispatch({
                type: 'SET_VALUE',
                value: form.coverDetail.driver.licenceAge.toString(),
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.driver.licenceAge'
            });
        }
        if (form.coverDetail?.annualKilometres !== undefined) {
            dispatch({
                type: 'SET_VALUE',
                value: form.coverDetail.annualKilometres.toString(),
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.annualKilometres'
            });
        }

        // Driver licence type and youngest driver licence -- This can only be tested once Car Journey changes are live
        if (form.coverDetail?.driver?.licenceType) {
            dispatch({
                type: 'SET_VALUE',
                value: form.coverDetail.driver.licenceType,
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.driver.licenceType'
            });
        }
        if (form.coverDetail?.driver?.comprehensiveInsured !== undefined) {
            dispatch({
                type: 'SET_VALUE',
                value: form.coverDetail.driver.comprehensiveInsured ? 'Y' : 'N',
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.driver.comprehensiveInsured'
            });
        }
        if (form.coverDetail?.driver?.comprehensiveInsuredYears !== undefined) {
            dispatch({
                type: 'SET_VALUE',
                value: form.coverDetail.driver.comprehensiveInsuredYears.toString(),
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.driver.comprehensiveInsuredYears'
            });
        }
        if (form.coverDetail?.youngestDriver?.licenceType) {
            dispatch({
                type: 'SET_VALUE',
                value: form.coverDetail.youngestDriver.licenceType,
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.youngestDriver.licenceType'
            });
        }
        if (form.coverDetail.passengersPayment !== undefined) {
            dispatch({
                type: 'SET_VALUE',
                value: form.coverDetail.passengersPayment ? 'Y' : 'N',
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.passengersPayment'
            });
        }
        if (form.coverDetail.goodsPayment !== undefined) {
            dispatch({
                type: 'SET_VALUE',
                value: form.coverDetail.goodsPayment ? 'Y' : 'N',
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.goodsPayment'
            });
        }
        if (form.coverDetail.ownsAnotherCar !== undefined) {
            dispatch({
                type: 'SET_VALUE',
                value: form.coverDetail.ownsAnotherCar ? 'Y' : 'N',
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.ownsAnotherCar'
            });
        }
        if (form.coverDetail.ownsHome !== undefined) {
            dispatch({
                type: 'SET_VALUE',
                value: form.coverDetail.ownsHome ? 'Y' : 'N',
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.ownsHome'
            });
        }
        if (form.coverDetail.vehicle.modified !== undefined) {
            dispatch({
                type: 'SET_VALUE',
                value: form.coverDetail.vehicle.modified ? 'Y' : 'N',
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.vehicle.modified'
            });
        }
        if (form.coverDetail.vehicle.damaged !== undefined) {
            dispatch({
                type: 'SET_VALUE',
                value: form.coverDetail.vehicle.damaged ? 'Y' : 'N',
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.vehicle.damaged'
            });
        }
        if (form.coverDetail.hasYoungerDriver !== undefined) {
            dispatch({
                type: 'SET_VALUE',
                value: form.coverDetail.hasYoungerDriver ? 'Y' : 'N',
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.hasYoungerDriver'
            });
        }
        if (form.coverDetail.currentlyInsured !== undefined) {
            dispatch({
                type: 'SET_VALUE',
                value: form.coverDetail.currentlyInsured ? 'Y' : 'N',
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.currentlyInsured'
            });
        }
        if (form.coverDetail?.driver?.claimsDetails !== undefined) {
            const driver = form.coverDetail?.driver;
            const claimsDetails = driver?.claimsDetails;
            const isHighRiskClaim = ['YES', 'UNSURE'].includes(driver?.anyPreviousClaims);
            dispatch({
                type: 'SET_VALUE',
                value: isHighRiskClaim ? null : claimsDetails.length.toString(),
                stateKey: 'vertical.quote',
                identifier: 'anyPreviousClaimsCount'
            });

            dispatch({
                type: 'SET_VALUE',
                value: isHighRiskClaim ? [{}] : claimsDetails,
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.driver.claimsDetails'
            });
        }

        // vehicle alarm and immobiliser
        dispatch({
            type: 'SET_MANUAL_LOOKUP_PAYLOAD',
            payload: form.coverDetail.vehicle.alarmFitted,
            identifier: 'alarmFitted'
        });
        dispatch({
            type: 'SET_MANUAL_LOOKUP_PAYLOAD',
            payload: form.coverDetail.vehicle.immobiliserFitted,
            identifier: 'immobiliserFitted'
        });

        if (address) {
            // Address label is required for non-manual address
            if (address.gnafId) {
                const addressLabel = formatAddress(address);
                dispatch({
                    type: 'SET_VALUE',
                    value: 'N',
                    stateKey: 'vertical.quote',
                    identifier: 'helpers.unableToFindAddress_overnightParking'
                });
                dispatch({
                    type: 'SET_VALUE',
                    value: addressLabel,
                    stateKey: 'vertical.quote',
                    identifier: 'helpers.overnightParkingAddressLabel'
                });
            } else {
                // Manual address
                dispatch({
                    type: 'SET_VALUE',
                    value: 'Y',
                    stateKey: 'vertical.quote',
                    identifier: 'helpers.unableToFindAddress_overnightParking'
                });
                if (address.buildingName !== undefined) {
                    dispatch({
                        type: 'SET_VALUE',
                        value: address.buildingName,
                        stateKey: 'vertical.quote',
                        identifier: 'coverDetail.overnightParking.address.buildingName'
                    });
                }
                if (address.unitNumber !== undefined) {
                    dispatch({
                        type: 'SET_VALUE',
                        value: address.unitNumber,
                        stateKey: 'vertical.quote',
                        identifier: 'coverDetail.overnightParking.address.unitNumber'
                    });
                }
                if (address.unitType !== undefined) {
                    dispatch({
                        type: 'SET_VALUE',
                        value: address.unitType,
                        stateKey: 'vertical.quote',
                        identifier: 'coverDetail.overnightParking.address.unitType'
                    });
                }
                if (address.streetNumber !== undefined) {
                    dispatch({
                        type: 'SET_VALUE',
                        value: address.streetNumber,
                        stateKey: 'vertical.quote',
                        identifier: 'coverDetail.overnightParking.address.streetNumber'
                    });
                }
                if (address.streetName !== undefined) {
                    dispatch({
                        type: 'SET_VALUE',
                        value: address.streetName,
                        stateKey: 'vertical.quote',
                        identifier: 'coverDetail.overnightParking.address.streetName'
                    });
                }
                if (address.postcode !== undefined) {
                    dispatch({
                        type: 'SET_VALUE',
                        value: address.postcode,
                        stateKey: 'vertical.quote',
                        identifier: 'coverDetail.overnightParking.address.postcode'
                    });
                }
                if (address.state !== undefined) {
                    dispatch({
                        type: 'SET_VALUE',
                        value: address.state,
                        stateKey: 'vertical.quote',
                        identifier: 'coverDetail.overnightParking.address.state'
                    });
                }
                if (address.suburb !== undefined) {
                    dispatch({
                        type: 'SET_VALUE',
                        value: address.suburb,
                        stateKey: 'vertical.quote',
                        identifier: 'coverDetail.overnightParking.address.suburb'
                    });
                }
            }
        } else {
            dispatch({
                type: 'SET_VALUE',
                value: {
                    unitType: null,
                    unitNumber: null,
                    buildingName: null,
                    streetNumber: null,
                    streetName: null,
                    state: null,
                    suburb: null,
                    postcode: null
                },
                stateKey: 'vertical.quote',
                identifier: 'coverDetail.overnightParking.address'
            });
        }

        // Requested excess
        // Returned from car journey service and set after address page if coverType
        // is COMPREHENSIVE - managed by journey so don't need to call setExcessByState.
        // High and low do not change.
        if (form.coverDetail.requestedExcess) {
            dispatch({
                type: 'SET_FORM_VALUE',
                identifier: 'coverDetail.requestedExcess',
                stateKey: 'vertical.quote',
                value: form.coverDetail.requestedExcess.toString()
            });
            dispatch({
                type: 'SET_RESULTS_CONFIG_VALUE',
                key: 'filters.requestedExcess',
                value: form.coverDetail.requestedExcess.toString()
            });
        }

        // coverType filter
        if (form.coverDetail.coverType) {
            dispatch({
                type: 'SET_RESULTS_CONFIG_VALUE',
                key: 'filters.coverType',
                value: form.coverDetail.coverType
            });
            dispatch({
                type: 'SET_RESULTS_CONFIG_VALUE',
                key: 'helpers.filters.coverType',
                value: form.coverDetail.coverType
            });
        }
    }

    // quote results
    if (payload?.quote?.payload?.results) {
        // results payload
        dispatch({
            type: 'SET_RESULTS_PAYLOAD',
            payload
        });
    }
};

const updateVehicleLookup = (dispatch, vehicleLookup) => {
    dispatch({
        type: 'SET_MANUAL_LOOKUP_PAYLOAD',
        payload: vehicleLookup.makes,
        identifier: 'makes'
    });
    dispatch({
        type: 'SET_MANUAL_LOOKUP_PAYLOAD',
        payload: vehicleLookup.years,
        identifier: 'years'
    });
    dispatch({
        type: 'SET_MANUAL_LOOKUP_PAYLOAD',
        payload: vehicleLookup.models,
        identifier: 'models'
    });
    dispatch({
        type: 'SET_MANUAL_LOOKUP_PAYLOAD',
        payload: vehicleLookup.bodies,
        identifier: 'bodies'
    });
    dispatch({
        type: 'SET_MANUAL_LOOKUP_PAYLOAD',
        payload: vehicleLookup.badge,
        identifier: 'badge'
    });
    dispatch({
        type: 'SET_MANUAL_LOOKUP_PAYLOAD',
        payload: vehicleLookup.transmissions,
        identifier: 'transmissions'
    });
    dispatch({
        type: 'SET_MANUAL_LOOKUP_PAYLOAD',
        payload: vehicleLookup.fuel,
        identifier: 'fuel'
    });
    dispatch({
        type: 'SET_MANUAL_LOOKUP_PAYLOAD',
        payload: vehicleLookup.description,
        identifier: 'description'
    });
    dispatch({
        type: 'SET_MANUAL_LOOKUP_PAYLOAD',
        payload: vehicleLookup.colours,
        identifier: 'colours'
    });
    dispatch({
        type: 'SET_MANUAL_LOOKUP_PAYLOAD',
        payload: vehicleLookup.factoryOptions,
        identifier: 'factoryOptions'
    });
    dispatch({
        type: 'SET_MANUAL_LOOKUP_PAYLOAD',
        payload: vehicleLookup.standardFeatures,
        identifier: 'standardFeatures'
    });
    dispatch({
        type: 'SET_MANUAL_LOOKUP_PAYLOAD',
        payload: vehicleLookup.series,
        identifier: 'series'
    });
};

const updatePrefill = (dispatch, payload) => {
    // if prefill object exists, remove any access tokens, before dispatching to state
    let prefill = {};
    if (payload.prefill) {
        prefill = payload.prefill;
    } else {
        prefill.attempt = 0;
        prefill.access_token = null;
        prefill.refresh_token = null;
    }
    dispatch({
        type: 'SET_VALUE',
        value: prefill,
        stateKey: 'vertical.quote',
        identifier: 'helpers.prefill'
    });

    // set these after dispatching the prefill api response
    if (payload.form.coverDetail.requestedExcess) {
        dispatch({
            type: 'SET_VALUE',
            identifier: 'helpers.prefill.previousExcessValue',
            stateKey: 'vertical.quote',
            value: payload.form.coverDetail.requestedExcess.toString()
        });
    }
    if (payload.form.coverDetail.coverType) {
        dispatch({
            type: 'SET_VALUE',
            identifier: 'helpers.prefill.previousCoverType',
            stateKey: 'vertical.quote',
            value: payload.form.coverDetail.coverType
        });
    }
};

// Utility to extract all valid touchable field identifiers from the schema
const extractTouchableIdentifiersFromSchema = function () {
    const identifiers = [];
    const validFieldTypes = [
        'badgegroup', 'radiogroup', 'formatted', 'formattedWithButtons', 'address', 'checkbox', 'input', 'component', 'validationMessage'
    ];
    // Excluding identifiers for saved data your_details, commencementDate and selection criteria for address.
    const excludeIdentifiers = [
        'property.address',
        'helpers.propertyPostCode',
        'occupancy.occupancyType',
        'coverDetails.coverType',
        'contactDetails.mobile',
        'contactDetails.phone',
        'coverDetails.commencementDate'
    ];
    // Get allElements from pageContext
    const allElements = pageContext.getProps().models.formSchemaModel.allElements;
    const recurse = function (elements) {
        if (!elements) return;
        elements.forEach((el) => {
            if (
                el.identifier
                && !excludeIdentifiers.includes(el.identifier)
                && ((el.field && validFieldTypes.includes(el.field.type)))
            ) {
                identifiers.push(el.identifier);
            }
            if (el.elements) recurse(el.elements);
        });
    };
    if (allElements && Array.isArray(allElements['vertical.quote'])) {
        recurse(allElements['vertical.quote']);
    }
    return Array.from(new Set(identifiers));
};

export const resetJourneyPayloads: { [key: string]: (dispatch: any) => void } = {
    'car-insurance': (dispatch) => resetCarJourneyPayload(dispatch),
    'home-contents-insurance': (dispatch) => resetHomeJourneyPayload(dispatch)
};

export const resetCarJourneyPayload = (dispatch) => {
    // Reset prefill values from any previous journeys within the session
    dispatch({
        type: 'SET_PAGE_VALUE',
        identifier: 'hasBeenPrefilled',
        value: false
    });
    dispatch({
        type: 'SET_PAGE_VALUE',
        identifier: 'hasSeenResults',
        value: false
    });
    dispatch({
        type: 'SET_RESULTS_CONFIG_VALUE',
        key: 'helpers.userHasSavedResults',
        value: false
    });
    // Clear journey + form + results
    dispatch({
        type: 'SET_JOURNEY_PAYLOAD',
        payload: { }
    });
    // clear verify response
    dispatch({
        type: 'SET_VERIFY_RESPONSE',
        payload: { }
    });
    dispatch({
        type: 'RESET_RESULTS'
    });
    dispatch({
        type: 'SET_VALUE',
        stateKey: 'vertical.quote',
        identifier: 'anyPreviousClaimsCount',
        value: null
    });
    dispatch({
        type: 'SET_VALUE',
        stateKey: 'vertical.quote',
        identifier: 'coverDetail',
        value: {
            useType: null,
            previousInsurer: null,
            requestedExcess: null,
            commencementDate: null,
            currentlyInsured: null,
            annualKilometres: null,
            driver: {
                lastName: null,
                gender: null,
                claimsDetails: null,
                licenceAge: null,
                dob: null,
                comprehensiveInsured: null,
                comprehensiveInsuredYears: null,
                anyPreviousClaims: null,
                firstName: null,
                employmentStatus: null
            },
            ownsHome: null,
            vehicle: {
                body: null,
                redbookCodeLong: null,
                badge: null,
                transmission: null,
                nonStandardAccessories: null,
                redbookCode: null,
                modified: null,
                model: null,
                glassCode: null,
                colour: null,
                nviCode: null,
                make: null,
                state: null,
                alarmFitted: null,
                factoryOptions: null,
                marketValue: null,
                damaged: null,
                fuel: null,
                immobiliserFitted: null,
                hasOtherAccessories: null,
                rego: null,
                description: null,
                year: null,
                securityFeatures: null
            },
            passengersPayment: null,
            goodsPayment: null,
            coverType: null,
            hasYoungerDriver: null,
            financeType: null,
            overnightParking: {
                parkingType: null,
                address: {
                    unitType: null,
                    unitNumber: null,
                    buildingName: null,
                    streetNumber: null,
                    streetName: null,
                    state: null,
                    suburb: null,
                    postcode: null
                }
            },
            youngestDriver: {
                licenceAge: null,
                dob: null,
                gender: null
            },
            driverOption: null,
            ownsAnotherCar: null
        }
    });
    dispatch({
        type: 'SET_VALUE',
        stateKey: 'vertical.quote',
        identifier: 'applicant',
        value: {
            optInPhone: null,
            optInEmail: null,
            optInPrivacy: null,
            email: null,
            phone: null,
            mobile: null,
            lastName: null,
            firstName: null
        }
    });
    // Clear form specifics
    dispatch({
        type: 'SET_VALUE',
        stateKey: 'vertical.quote',
        identifier: 'helpers.overnightParkingAddressLabel',
        value: null
    });
    dispatch({
        type: 'RESET_TOUCHES',
        stateKey: 'vertical.quote',
        identifiers: [
            'coverDetail.annualKilometres',
            'coverDetail.driver.dob',
            'coverDetail.driver.licenceAge',
            'coverDetail.overnightParking.address.postcode',
            'applicant.firstName',
            'applicant.lastName',
            'helpers.overnightParkingPostcode',
            'helpers.overnightParkingAddressLabel',
            'anyPreviousClaimsCount',
            'helpers.regoLabel',
            'firstPage.vehicle.rego'
        ]
    });
    dispatch({
        type: 'SET_VALUE',
        stateKey: 'vertical.quote',
        identifier: 'helpers.regoLabel',
        value: null
    });
};

export const  resetHomeJourneyPayload = (dispatch) => {
    // Reset prefill values from any previous journeys within the session
    dispatch({
        type: 'SET_PAGE_VALUE',
        identifier: 'hasBeenPrefilled',
        value: false
    });
    dispatch({
        type: 'SET_PAGE_VALUE',
        identifier: 'hasSeenResults',
        value: false
    });
    dispatch({
        type: 'SET_RESULTS_CONFIG_VALUE',
        key: 'helpers.userHasSavedResults',
        value: false
    });
    // Clear journey + form + results
    dispatch({
        type: 'SET_JOURNEY_PAYLOAD',
        payload: { }
    });
    dispatch({
        type: 'RESET_RESULTS'
    });
    dispatch({
        type: 'SET_VALUE',
        stateKey: 'vertical.quote',
        identifier: 'occupancy',
        value: {
            isOldestResidentRetired: null,
            howOccupied: null,
            whenMovedIn: {
                month: null,
                year: null
            },
            isPrincipalResidence: null,
            hasMortgage: null,
            occupancyType: null
        }
    });
    dispatch({
        type: 'SET_VALUE',
        stateKey: 'vertical.quote',
        identifier: 'property',
        value: {
            yearBuilt: null,
            hasBodyCorporate: null,
            propertyType: null,
            wallMaterial: null,
            heritageOverlay: null,
            address: {
                suburb: null,
                gnafId: null,
                streetNumber: null,
                postcode: null,
                streetName: null,
                state: null
            }
        }
    });
    dispatch({
        type: 'SET_VALUE',
        stateKey: 'vertical.quote',
        identifier: 'coverDetails',
        value: {
            commencementDate: null,
            validationMessage: null,
            isAbovePolicyLimits: null,
            totalContentReplacementCost: null,
            totalRebuildCost: null,
            coverType: null
        }
    });
    dispatch({
        type: 'SET_VALUE',
        stateKey: 'vertical.quote',
        identifier: 'data',
        value: {
            hasPortableItems: null,
            hasSecurityAlarm: null,
            hasBusinessActivity: null
        }
    });
    dispatch({
        type: 'SET_VALUE',
        stateKey: 'vertical.quote',
        identifier: 'helpers.propertyAddressLabel',
        value: null
    });
    dispatch({
        type: 'SET_VALUE',
        stateKey: 'vertical.quote',
        identifier: 'policyHolder',
        value: {
            dob: null,
            lastName: null,
            firstName: null
        }
    });
    dispatch({
        type: 'RESET_TOUCHES',
        stateKey: 'vertical.quote',
        identifiers: [
            'occupancy.occupancyType',
            'coverDetails.coverType'
        ]
    });
};

export const resetHomeJourneyPayloadForAddress = (dispatch, schema) => {
    // Reset prefill values from any previous journeys within the session
    dispatch({
        type: 'SET_PAGE_VALUE',
        identifier: 'hasBeenPrefilled',
        value: false
    });
    dispatch({
        type: 'SET_PAGE_VALUE',
        identifier: 'hasSeenResults',
        value: false
    });
    dispatch({
        type: 'SET_RESULTS_CONFIG_VALUE',
        key: 'helpers.userHasSavedResults',
        value: false
    });
    dispatch({
        type: 'RESET_RESULTS'
    });
    // Automated null reset for all touchable identifiers except address and nested objects
    const allIdentifiers = extractTouchableIdentifiersFromSchema().map((id) => (typeof id === 'string' ? id.trim() : String(id).trim()));
    // Group identifiers by their parent (e.g. landlordDetails.*)
    const parentMap = {};
    allIdentifiers.forEach((identifier) => {
        const parts = identifier.split('.');
        if (parts.length > 1) {
            const parent = parts[0];
            if (!parentMap[parent]) parentMap[parent] = new Set();
            parentMap[parent].add(parts.slice(1).join('.'));
        }
    });
    // For each parent, if there are multiple subfields, build an object with all subfields set to null
    Object.keys(parentMap).forEach((parent) => {
        if (parentMap[parent].size > 1) {
            const nullObj = {};
            parentMap[parent].forEach((sub) => {
                if (sub && !sub.includes('.')) nullObj[sub] = null;
            });
            if (Object.keys(nullObj).length > 0) {
                dispatch({
                    type: 'SET_VALUE',
                    stateKey: 'vertical.quote',
                    identifier: parent,
                    value: nullObj
                });
            }
        }
    });
    // For identifiers not handled as parent objects, set to null
    allIdentifiers.forEach((identifier) => {
        const parts = identifier.split('.');
        if (parts.length === 1 || (parentMap[parts[0]] && parentMap[parts[0]].size === 1)) {
            dispatch({
                type: 'SET_VALUE',
                stateKey: 'vertical.quote',
                identifier,
                value: null
            });
        }
    });
    // Reset all touches for all valid form fields using improved schema parsing
    dispatch({
        type: 'RESET_TOUCHES',
        stateKey: 'vertical.quote',
        identifiers: allIdentifiers
    });
};

/**
 * Get days since lastAccessedDate string
 * @param {string?} lastAccessedDate - starting date
 * @returns {string} returns 'today', 'yesterday', or 'X days ago' string
 */
const _getDaysAgoText = (lastAccessedDate) => {
    if (!lastAccessedDate) return '';

    const localServerMoment = moment.utc(lastAccessedDate).local();

    const daysAgo = moment().startOf('day').diff(localServerMoment.startOf('day'), 'days');

    if (isNaN(daysAgo)) { return null; }

    if (daysAgo === 0) { return 'today'; }
    if (daysAgo === 1) { return 'yesterday'; }

    return `${daysAgo} days ago`;
};

/**
 * Get saved days since lastAccessedDate string
 * @param {string?} lastAccessedDate - phrase of days since last updated
 * @returns {string | null} returns 'Saved today', 'Saved 1 day ago', or 'Saved X days ago' string
 */
const getSavedSince = (lastAccessedDate) => {
    const daysAgo = _getDaysAgoText(lastAccessedDate);
    if (!daysAgo || daysAgo.length === 0) return null;

    return `Saved ${daysAgo}`;
};

export const PREFILL_INELIGIBLE_BRANDS = ['choo', 'islt'];
export const REDIRECT_DELAY_MS = 4000;

export { updateState, updateJourney, updateVehicleLookup, updateTokens, updatePrefill, getSavedSince };
